import React, { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Header from '../../blocks/header';
import Instruction from '../../themed/instruction-modals/instruction';
import ResetPasswordForm from '../../forms/resetPasswordForm';
import SyncTime from '../../blocks/sync-time';

import { User } from '../../../utils/models';
import { matchPhrases } from '../../../utils/utilities';
import { USER_TYPES, tutorialConstants } from '../../../utils/constants';

import { getCurrentUser } from '../../../services/authService';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';

import classNames from './style.module.scss';
import routes from '../../../utils/routes';

const ProfileSettings: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const user: User = getCurrentUser() as User;
	const location = useLocation();
	const tutorials = useAppSelector(getTutorials);
	const signatureRef = useRef<HTMLDivElement>(null);

	const handleTutorialCompleted = () => {
		dispatch(setActiveTutorial(tutorialConstants.VIEW_TUTORIALS));
		navigate(routes.tutorials.fullPath);
	};

	const renderDivision = () => {
		const isInternalUser = matchPhrases(user.userType, USER_TYPES.INTERNAL);

		return (
			<span className={classNames.state}>
				{isInternalUser ? user.divisionName.join(', ') : user.roleName}
			</span>
		);
	};

	useLayoutEffect(() => {
		if (location.state?.fromDashboard) {
			const signatureForm = signatureRef.current;
			if (signatureForm) signatureForm.scrollIntoView({ behavior: 'smooth' });
		}
	}, [location.state]);

	return (
		<React.Fragment>
			<Instruction
				isVisible={tutorials.activeTutorial === tutorialConstants.PROFILE_SETTINGS}
				onProceed={handleTutorialCompleted}
				title="Update Your Info Here!"
				message="You can configure your signature and update your profile password here. Simply fill any of the forms below!"
			/>

			<Header subtitle={'Showing User Details for User: ' + user?.firstName} />

			<div className={classNames.users}>
				<div className="d-flex justify-content-start align-items-center">
					<div className={classNames.userDetailsIcon}>
						<div className={classNames.userDetailsIconBackground}>
							<i className="bi bi-person-bounding-box"></i>
						</div>
					</div>

					<div className={classNames.userDetailsBody}>
						<h3>
							{user.firstName} {user.lastName}
						</h3>
						<div className={classNames.userDetailsBodyAddress}>
							{user.designation}
						</div>
						<div className={classNames.userDetailsBodySubdetails}>
							{renderDivision()}
							<span className={classNames.phone + ' px-2'}>
								{user.phone} | {user.email}
							</span>
						</div>
					</div>

					<div className={classNames.userDetailsRole}>{user.userType}</div>
				</div>

				<div className="divider my-4"></div>

				<SyncTime
					label="Last Login"
					lastSynced={new Date(user.lastLogin as string).getTime()}
				/>

				<div className="divider my-4"></div>

				<div className={classNames.usersSignature}>
					<ResetPasswordForm />
				</div>
			</div>
		</React.Fragment>
	);
};

export default ProfileSettings;
